/**
 * The MB Group custom-element set. Every element renders into a shadow root, adopts
 * the shared design tokens, is keyboard-operable and labelled for assistive tech, and
 * takes its UI copy from `@mb-group/i18n` keyed by the element's `locale` attribute.
 */
import type { NavigationItem } from "@mb-group/shared-types";
import { MbElement, esc } from "./mb-element.js";
import { i18nFor } from "./i18n-helper.js";

// --- <mb-skip-link> ---------------------------------------------------------
class MbSkipLink extends MbElement {
	static override get observedAttributes(): string[] {
		return ["locale", "href"];
	}
	protected override styles(): string {
		return /* css */ `
      a {
        position: absolute; left: -9999px; top: 0; z-index: 1000;
        background: var(--mb-brand, #14201b); color: var(--mb-on-brand, #fff);
        padding: 0.6rem 1rem; text-decoration: none;
        border-radius: 0 0 var(--mb-radius-lg, 10px) 0;
      }
      a:focus { left: 0; }
    `;
	}
	protected override render(): string {
		const i18n = i18nFor(this.locale);
		return `<a href="${esc(this.attr("href", "#main"))}">${esc(i18n.t("common.skipToContent"))}</a>`;
	}
}

// --- <mb-button> ------------------------------------------------------------
class MbButton extends MbElement {
	static override get observedAttributes(): string[] {
		return ["variant", "size", "href", "type", "disabled", "full"];
	}
	protected override styles(): string {
		return /* css */ `
      :host { display: inline-block; }
      :host([full]) { display: block; }
      .btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem;
        font: inherit; font-weight: 600; line-height: 1; cursor: pointer;
        border: 1px solid transparent; border-radius: var(--mb-radius-lg, 10px);
        padding: 0.75rem 1.25rem; text-decoration: none; width: 100%;
        transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
      }
      .s-sm { padding: 0.45rem 0.8rem; font-size: 0.875rem; }
      .s-lg { padding: 0.95rem 1.6rem; font-size: 1.075rem; }
      .v-primary { background: var(--mb-brand, #14201b); color: var(--mb-on-brand, #fff); }
      .v-primary:hover { background: var(--mb-brand-strong, #0d1712); }
      .v-secondary { background: var(--mb-accent, #b6e34b); color: var(--mb-on-accent, #14201b); }
      .v-ghost { background: transparent; color: var(--mb-text, #14201b); border-color: var(--mb-border, rgba(0,0,0,.2)); }
      .v-ghost:hover { background: var(--mb-bg-sunken, rgba(0,0,0,.05)); }
      .btn[disabled], .btn[aria-disabled="true"] { opacity: 0.55; cursor: not-allowed; }
    `;
	}
	protected override render(): string {
		const variant = this.attr("variant", "primary");
		const size = this.attr("size", "md");
		const disabled = this.hasAttribute("disabled");
		const href = this.getAttribute("href");
		const cls = `btn v-${esc(variant)} s-${esc(size)}`;
		if (href && !disabled) {
			return `<a class="${cls}" href="${esc(href)}"><slot></slot></a>`;
		}
		const type = this.attr("type", "button");
		const dis = disabled ? " disabled" : "";
		return `<button class="${cls}" type="${esc(type)}"${dis}><slot></slot></button>`;
	}
}

// --- <mb-badge> -------------------------------------------------------------
class MbBadge extends MbElement {
	static override get observedAttributes(): string[] {
		return ["variant", "label", "locale"];
	}
	protected override styles(): string {
		return /* css */ `
      .badge {
        display: inline-flex; align-items: center; gap: 0.35rem;
        font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
        padding: 0.2rem 0.5rem; border-radius: 999px; line-height: 1.2;
        background: var(--mb-bg-sunken, #eef1ee); color: var(--mb-text-muted, #556);
      }
      .v-demo { background: var(--mb-accent, #b6e34b); color: var(--mb-on-accent, #14201b); }
      .v-success { background: var(--mb-success-bg, #e3f6e6); color: var(--mb-text, #14201b); }
      .v-danger { background: var(--mb-danger-bg, #fdeaea); color: var(--mb-text, #14201b); }
    `;
	}
	protected override render(): string {
		const variant = this.attr("variant", "info");
		const i18n = i18nFor(this.locale);
		const label = this.getAttribute("label");
		let content: string;
		if (variant === "demo") content = esc(i18n.t("common.demoBadge"));
		else if (label !== null) content = esc(label);
		else content = "<slot></slot>";
		return `<span class="badge v-${esc(variant)}">${content}</span>`;
	}
}

// --- <mb-header> ------------------------------------------------------------
class MbHeader extends MbElement {
	protected override styles(): string {
		return /* css */ `
      .hdr { background: var(--mb-bg-elevated, #fff); border-bottom: 1px solid var(--mb-border, rgba(0,0,0,.12)); }
      .inner {
        max-width: var(--mb-container-max, 1200px); margin: 0 auto; padding: 0.75rem 1rem;
        display: flex; align-items: center; gap: 1rem;
      }
      .brand { display: flex; align-items: center; }
      .nav { flex: 1; display: flex; justify-content: center; }
      .actions { display: flex; align-items: center; gap: 0.5rem; }
    `;
	}
	protected override render(): string {
		return `
      <header class="hdr" role="banner">
        <div class="inner">
          <div class="brand"><slot name="logo"></slot></div>
          <div class="nav"><slot name="nav"></slot></div>
          <div class="actions"><slot name="actions"></slot></div>
        </div>
      </header>`;
	}
}

// --- <mb-footer> ------------------------------------------------------------
class MbFooter extends MbElement {
	protected override styles(): string {
		return /* css */ `
      .ftr { background: var(--mb-bg-inverse, #14201b); color: var(--mb-text-inverse, #fff); }
      .inner {
        max-width: var(--mb-container-max, 1200px); margin: 0 auto; padding: 2.5rem 1rem;
        display: grid; gap: 1.5rem; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      }
      .legal { border-top: 1px solid rgba(255,255,255,0.15); padding: 1rem; text-align: center; font-size: 0.85rem; opacity: 0.85; }
    `;
	}
	protected override render(): string {
		return `
      <footer class="ftr" role="contentinfo">
        <div class="inner"><slot></slot></div>
        <div class="legal"><slot name="legal"></slot></div>
      </footer>`;
	}
}

// --- <mb-nav> ---------------------------------------------------------------
class MbNav extends MbElement {
	#items: NavigationItem[] = [];
	#open = false;

	static override get observedAttributes(): string[] {
		return ["label", "current", "locale"];
	}

	override connectedCallback(): void {
		this.upgradeProperty("items");
		super.connectedCallback();
	}

	set items(value: NavigationItem[]) {
		this.#items = Array.isArray(value) ? value : [];
		this.update();
	}
	get items(): NavigationItem[] {
		return this.#items;
	}

	protected override styles(): string {
		return /* css */ `
      nav { position: relative; }
      .toggle {
        display: none; font: inherit; background: transparent; border: 1px solid var(--mb-border, #ccc);
        border-radius: var(--mb-radius-sm, 6px); padding: 0.5rem 0.75rem; cursor: pointer; color: inherit;
      }
      ul { list-style: none; margin: 0; padding: 0; display: flex; gap: 0.25rem; flex-wrap: wrap; }
      li { position: relative; }
      a {
        display: block; padding: 0.5rem 0.75rem; text-decoration: none; color: inherit;
        border-radius: var(--mb-radius-sm, 6px);
      }
      a:hover { background: var(--mb-bg-sunken, rgba(0,0,0,.05)); }
      a[aria-current="page"] { font-weight: 700; color: var(--mb-brand, #14201b); }
      ul ul { position: absolute; display: none; flex-direction: column; background: var(--mb-bg-elevated,#fff);
        border: 1px solid var(--mb-border,#ddd); border-radius: var(--mb-radius-sm,6px); min-width: 12rem; padding: 0.25rem; z-index: 20; }
      li:hover > ul, li:focus-within > ul { display: flex; }
      @media (max-width: 720px) {
        .toggle { display: inline-block; }
        ul.list { display: none; flex-direction: column; }
        :host([data-open="true"]) ul.list, ul.list.open { display: flex; }
      }
    `;
	}

	#renderItems(items: NavigationItem[], current: string): string {
		return items
			.map((item) => {
				const aria = item.href === current ? ' aria-current="page"' : "";
				const children =
					item.children && item.children.length > 0
						? `<ul>${this.#renderItems(item.children, current)}</ul>`
						: "";
				return `<li><a href="${esc(item.href)}"${aria}>${esc(item.label)}</a>${children}</li>`;
			})
			.join("");
	}

	protected override render(): string {
		const i18n = i18nFor(this.locale);
		const label = this.attr("label", i18n.t("nav.services"));
		const current = this.attr("current");
		const openCls = this.#open ? " open" : "";
		return `
      <nav aria-label="${esc(label)}">
        <button class="toggle" type="button" aria-expanded="${String(this.#open)}" aria-controls="mb-nav-list">
          ${esc(i18n.t("common.menu"))}
        </button>
        <ul id="mb-nav-list" class="list${openCls}">${this.#renderItems(this.#items, current)}</ul>
      </nav>`;
	}

	protected override afterRender(): void {
		const toggle = this.root.querySelector<HTMLButtonElement>(".toggle");
		toggle?.addEventListener("click", () => {
			this.#open = !this.#open;
			this.setAttribute("data-open", String(this.#open));
			this.update();
		});
	}
}

// --- <mb-theme-switcher> ----------------------------------------------------
class MbThemeSwitcher extends MbElement {
	static override get observedAttributes(): string[] {
		return ["value", "locale"];
	}
	protected override styles(): string {
		return switcherStyles();
	}
	protected override render(): string {
		const i18n = i18nFor(this.locale);
		const value = this.attr("value", "system");
		const options: [string, string][] = [
			["light", i18n.t("switcher.light")],
			["dark", i18n.t("switcher.dark")],
			["system", i18n.t("switcher.system")],
		];
		return `
      <div class="group" role="group" aria-label="${esc(i18n.t("switcher.theme"))}">
        ${options
					.map(
						([key, text]) =>
							`<button type="button" data-value="${key}" aria-pressed="${String(key === value)}">${esc(text)}</button>`,
					)
					.join("")}
      </div>`;
	}
	protected override afterRender(): void {
		bindGroup(this.root, (value) => {
			this.setAttribute("value", value);
			this.emit<{ preference: string }>("mb:theme-change", { preference: value });
		});
	}
}

// --- <mb-lang-switcher> -----------------------------------------------------
class MbLangSwitcher extends MbElement {
	static override get observedAttributes(): string[] {
		return ["value", "locale"];
	}
	protected override styles(): string {
		return switcherStyles();
	}
	protected override render(): string {
		const i18n = i18nFor(this.locale);
		const value = this.attr("value", this.locale);
		const options: [string, string][] = [
			["pl", i18n.t("switcher.polish")],
			["en", i18n.t("switcher.english")],
		];
		return `
      <div class="group" role="group" aria-label="${esc(i18n.t("switcher.language"))}">
        ${options
					.map(
						([key, text]) =>
							`<button type="button" data-value="${key}" aria-pressed="${String(key === value)}" lang="${key}">${esc(text)}</button>`,
					)
					.join("")}
      </div>`;
	}
	protected override afterRender(): void {
		bindGroup(this.root, (value) => {
			this.setAttribute("value", value);
			this.emit<{ locale: string }>("mb:locale-change", { locale: value });
		});
	}
}

// --- <mb-card> --------------------------------------------------------------
class MbCard extends MbElement {
	static override get observedAttributes(): string[] {
		return ["href", "demo", "locale"];
	}
	protected override styles(): string {
		return /* css */ `
      .card {
        display: flex; flex-direction: column; overflow: hidden; text-decoration: none; color: inherit;
        background: var(--mb-bg-elevated, #fff); border: 1px solid var(--mb-border, rgba(0,0,0,.12));
        border-radius: var(--mb-radius-lg, 12px); box-shadow: var(--mb-shadow-sm, 0 1px 2px rgba(0,0,0,.08));
        transition: box-shadow 140ms ease, transform 140ms ease; height: 100%;
      }
      a.card:hover { box-shadow: var(--mb-shadow-md, 0 12px 32px rgba(0,0,0,.12)); transform: translateY(-2px); }
      .media { display: block; }
      .media ::slotted(img) { width: 100%; height: auto; display: block; }
      .body { padding: 1.1rem 1.25rem; display: flex; flex-direction: column; gap: 0.5rem; flex: 1; }
      .demo-badge {
        align-self: flex-start; font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
        letter-spacing: 0.04em; padding: 0.15rem 0.45rem; border-radius: 999px;
        background: var(--mb-accent, #b6e34b); color: var(--mb-on-accent, #14201b);
      }
      .foot { margin-top: auto; }
    `;
	}
	protected override render(): string {
		const i18n = i18nFor(this.locale);
		const href = this.getAttribute("href");
		const demo = this.hasAttribute("demo")
			? `<span class="demo-badge">${esc(i18n.t("common.demoBadge"))}</span>`
			: "";
		const inner = `
      <div class="media"><slot name="media"></slot></div>
      <div class="body">
        ${demo}
        <slot name="title"></slot>
        <slot></slot>
        <div class="foot"><slot name="footer"></slot></div>
      </div>`;
		return href
			? `<a class="card" href="${esc(href)}">${inner}</a>`
			: `<article class="card">${inner}</article>`;
	}
}

// --- <mb-search-box> --------------------------------------------------------
class MbSearchBox extends MbElement {
	static override get observedAttributes(): string[] {
		return ["value", "action", "name", "locale"];
	}
	protected override styles(): string {
		return /* css */ `
      form { display: flex; gap: 0.4rem; }
      input {
        flex: 1; font: inherit; padding: 0.6rem 0.8rem; color: inherit;
        border: 1px solid var(--mb-border, #ccc); border-radius: var(--mb-radius-lg, 10px);
        background: var(--mb-bg, #fff);
      }
      button {
        font: inherit; font-weight: 600; cursor: pointer; padding: 0.6rem 1rem;
        border: 1px solid transparent; border-radius: var(--mb-radius-lg, 10px);
        background: var(--mb-brand, #14201b); color: var(--mb-on-brand, #fff);
      }
    `;
	}
	protected override render(): string {
		const i18n = i18nFor(this.locale);
		const value = this.attr("value");
		const name = this.attr("name", "q");
		const label = i18n.t("common.search");
		return `
      <form role="search">
        <label class="sr-only" for="mb-search-input">${esc(label)}</label>
        <input id="mb-search-input" name="${esc(name)}" type="search"
               value="${esc(value)}" placeholder="${esc(i18n.t("common.searchPlaceholder"))}"
               autocomplete="off" />
        <button type="submit">${esc(label)}</button>
      </form>`;
	}
	protected override afterRender(): void {
		const form = this.root.querySelector<HTMLFormElement>("form");
		const input = this.root.querySelector<HTMLInputElement>("input");
		form?.addEventListener("submit", (event) => {
			event.preventDefault();
			const query = input?.value.trim() ?? "";
			this.emit<{ query: string }>("mb:search", { query });
		});
	}
}

// --- <mb-ai-assistant> ------------------------------------------------------
class MbAiAssistant extends MbElement {
	#open = false;
	static override get observedAttributes(): string[] {
		return ["locale"];
	}
	protected override styles(): string {
		return /* css */ `
      .launch {
        font: inherit; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 0.5rem;
        padding: 0.6rem 1rem; border-radius: 999px; border: 1px solid transparent;
        background: var(--mb-brand, #14201b); color: var(--mb-on-brand, #fff);
      }
      .panel {
        margin-top: 0.75rem; padding: 1rem; border: 1px solid var(--mb-border, #ddd);
        border-radius: var(--mb-radius-lg, 12px); background: var(--mb-bg-elevated, #fff);
        max-width: 28rem;
      }
      .row { display: flex; gap: 0.4rem; margin-top: 0.5rem; }
      .row input { flex: 1; font: inherit; padding: 0.5rem 0.7rem; border: 1px solid var(--mb-border, #ccc); border-radius: var(--mb-radius-sm, 6px); }
      .disclaimer { font-size: 0.8rem; color: var(--mb-text-muted, #667); margin-top: 0.6rem; }
    `;
	}
	protected override render(): string {
		const i18n = i18nFor(this.locale);
		const title = i18n.t("assistant.title");
		const panel = this.#open
			? `
        <div class="panel" id="mb-assistant-panel" role="region" aria-label="${esc(title)}">
          <strong>${esc(title)}</strong>
          <form class="row">
            <label class="sr-only" for="mb-assistant-input">${esc(title)}</label>
            <input id="mb-assistant-input" type="text" placeholder="${esc(i18n.t("assistant.placeholder"))}" />
            <button type="submit" class="launch">${esc(i18n.t("common.search"))}</button>
          </form>
          <p class="disclaimer">${esc(i18n.t("assistant.disclaimer"))}</p>
        </div>`
			: "";
		return `
      <button class="launch" type="button" aria-expanded="${String(this.#open)}" aria-controls="mb-assistant-panel">
        ${esc(title)}
      </button>
      ${panel}`;
	}
	protected override afterRender(): void {
		const launch = this.root.querySelector<HTMLButtonElement>(".launch");
		launch?.addEventListener("click", () => {
			this.#open = !this.#open;
			this.update();
		});
		const form = this.root.querySelector<HTMLFormElement>(".panel form");
		const input = this.root.querySelector<HTMLInputElement>("#mb-assistant-input");
		form?.addEventListener("submit", (event) => {
			event.preventDefault();
			const query = input?.value.trim() ?? "";
			this.emit<{ query: string }>("mb:assistant-query", { query });
		});
	}
}

// --- <mb-toast> -------------------------------------------------------------
class MbToast extends MbElement {
	#timer: ReturnType<typeof setTimeout> | undefined;
	static override get observedAttributes(): string[] {
		return ["open", "variant", "message", "duration", "locale"];
	}
	protected override styles(): string {
		return /* css */ `
      .live { position: fixed; inset-inline: 0; bottom: 1rem; display: flex; justify-content: center; pointer-events: none; z-index: 1000; }
      .toast {
        pointer-events: auto; display: flex; align-items: center; gap: 0.75rem;
        max-width: min(92vw, 32rem); padding: 0.8rem 1rem; border-radius: var(--mb-radius-lg, 10px);
        background: var(--mb-bg-inverse, #14201b); color: var(--mb-text-inverse, #fff);
        box-shadow: var(--mb-shadow-md, 0 12px 32px rgba(0,0,0,.25));
      }
      .v-success { background: var(--mb-brand, #1c5d3a); }
      .v-danger { background: var(--mb-danger, #c0392b); }
      .msg { flex: 1; }
      .close { font: inherit; cursor: pointer; background: transparent; border: 0; color: inherit; font-size: 1.2rem; line-height: 1; }
    `;
	}
	protected override render(): string {
		const open = this.hasAttribute("open");
		const variant = this.attr("variant", "default");
		const message = this.getAttribute("message");
		const i18n = i18nFor(this.locale);
		const body = open
			? `<div class="toast v-${esc(variant)}">
           <span class="msg">${message !== null ? esc(message) : "<slot></slot>"}</span>
           <button class="close" type="button" aria-label="${esc(i18n.t("common.close"))}">&times;</button>
         </div>`
			: "";
		return `<div class="live" role="status" aria-live="polite" aria-atomic="true">${body}</div>`;
	}
	protected override afterRender(): void {
		if (this.#timer !== undefined) {
			clearTimeout(this.#timer);
			this.#timer = undefined;
		}
		const close = this.root.querySelector<HTMLButtonElement>(".close");
		close?.addEventListener("click", () => this.dismiss());

		if (this.hasAttribute("open")) {
			const duration = Number(this.getAttribute("duration") ?? "0");
			if (Number.isFinite(duration) && duration > 0) {
				this.#timer = setTimeout(() => this.dismiss(), duration);
			}
		}
	}
	protected override onDisconnect(): void {
		if (this.#timer !== undefined) clearTimeout(this.#timer);
	}
	private dismiss(): void {
		this.removeAttribute("open");
		this.emit("mb:toast-dismiss");
	}
}

// --- shared switcher helpers ------------------------------------------------
function switcherStyles(): string {
	return /* css */ `
    .group { display: inline-flex; border: 1px solid var(--mb-border, #ccc); border-radius: 999px; padding: 2px; gap: 2px; }
    button {
      font: inherit; font-size: 0.85rem; cursor: pointer; border: 0; background: transparent; color: inherit;
      padding: 0.35rem 0.7rem; border-radius: 999px; line-height: 1.2;
    }
    button[aria-pressed="true"] { background: var(--mb-brand, #14201b); color: var(--mb-on-brand, #fff); }
  `;
}

function bindGroup(root: ShadowRoot, onSelect: (value: string) => void): void {
	root.querySelectorAll<HTMLButtonElement>("button[data-value]").forEach((button) => {
		button.addEventListener("click", () => {
			const value = button.getAttribute("data-value");
			if (value) onSelect(value);
		});
	});
}

/** Tag → constructor map for every element in this package. */
export const ELEMENTS: Record<string, CustomElementConstructor> = {
	"mb-skip-link": MbSkipLink,
	"mb-button": MbButton,
	"mb-badge": MbBadge,
	"mb-header": MbHeader,
	"mb-footer": MbFooter,
	"mb-nav": MbNav,
	"mb-theme-switcher": MbThemeSwitcher,
	"mb-lang-switcher": MbLangSwitcher,
	"mb-card": MbCard,
	"mb-search-box": MbSearchBox,
	"mb-ai-assistant": MbAiAssistant,
	"mb-toast": MbToast,
};

export {
	MbSkipLink,
	MbButton,
	MbBadge,
	MbHeader,
	MbFooter,
	MbNav,
	MbThemeSwitcher,
	MbLangSwitcher,
	MbCard,
	MbSearchBox,
	MbAiAssistant,
	MbToast,
};
